import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { Popover, Tooltip } from "antd";
import { CheckOutlined, DownOutlined, HistoryOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  compassBordeaux,
  compassLightGreen,
  compassLightGreenBackground,
  darkGreen,
  greyText,
  lightGreyOutline,
  lightGreys,
} from "../../../../styles";
import {
  AssessmentStateV2,
  EngagementAssessmentV2Run,
} from "../../../../types/engagementAssessmentV2Types";

import { FlagGlyph, FlagStack, stateInk } from "./assessmentAtoms";
import { AssessmentViewModel, buildViewModel } from "./assessmentViewModel";

dayjs.extend(relativeTime);

const DATE_FORMAT = "MMM D";
const DATE_TIME_FORMAT = "MMM D, YYYY · HH:mm";

const Bar = styled.div`
  align-self: center;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 30px;
  padding: 0 12px;
  box-sizing: border-box;
  border-radius: 999px;
  background-color: white;
  box-shadow: inset 0 0 0 1px ${lightGreyOutline};
  font-family: "Apercu";
  font-size: 12px;
  color: ${darkGreen};
  white-space: nowrap;
  cursor: pointer;
  min-width: 0;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    box-shadow:
      inset 0 0 0 1px ${lightGreyOutline},
      0 6px 18px rgba(21, 50, 48, 0.07);
    transform: translateY(-1px);
  }
  &:focus-visible {
    outline: 2px solid ${compassLightGreen};
    outline-offset: 2px;
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 12px;
  background: ${lightGreyOutline};
  flex-shrink: 0;
`;

const Item = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const Muted = styled.span`
  color: ${greyText};
`;

const Icon = styled.span`
  display: inline-flex;
  font-size: 12px;
  color: ${greyText};
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const Primary = styled.span`
  font-family: "Apercu";
  font-size: 12.5px;
  color: ${darkGreen};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Secondary = styled.span`
  font-family: "Apercu";
  font-size: 10.5px;
  color: ${greyText};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Mono = styled.span`
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 11.5px;
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  font-size: 8px;
  transition: transform 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
  transform: rotate(${({ $open }): string => ($open ? "180deg" : "0deg")});

  ${Bar}:hover & {
    transform: rotate(${({ $open }): string => ($open ? "180deg" : "0deg")})
      translateY(1px);
  }
`;

const grow = keyframes`
  from { transform: scaleY(0.2); opacity: 0; }
  to { transform: scaleY(1); opacity: 1; }
`;

const Segments = styled.span`
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 10px;
`;

const Segment = styled.span<{ $tint: string; $index: number }>`
  width: 5px;
  height: 10px;
  border-radius: 1.5px;
  background: ${({ $tint }): string => $tint};
  transform-origin: bottom;
  animation: ${grow} 0.35s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  animation-delay: ${({ $index }): number => 120 + $index * 45}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const FlagMark = styled.span<{ $flagged: boolean }>`
  display: inline-flex;
  font-size: 10px;
  color: ${({ $flagged }): string =>
    $flagged ? compassBordeaux : compassLightGreen};
`;

export const StateSegments = ({
  states,
}: {
  states: AssessmentStateV2[];
}): JSX.Element => (
  <Segments aria-hidden="true">
    {states.map((s, i) => (
      <Segment key={i} $index={i} $tint={stateInk(s)} />
    ))}
  </Segments>
);

const List = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
  margin: -4px -8px;
  min-width: 440px;
`;

const RunRow = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-radius: 6px;
  cursor: pointer;
  background: ${({ $active }): string =>
    $active ? compassLightGreenBackground : "transparent"};
  transition: background 0.12s ease;

  &:hover {
    background: ${({ $active }): string =>
      $active ? compassLightGreenBackground : lightGreys.KPIBackground};
  }
  & + & {
    margin-top: 2px;
  }
`;

const Spacer = styled.span`
  flex: 1;
`;

const ListHeader = styled.div`
  padding: 4px 12px 8px;
  margin-bottom: 4px;
  border-bottom: 1px solid ${lightGreyOutline};
  font-family: "ApercuMedium";
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${greyText};
`;

export const AssessmentRunBar = ({
  runs,
  view,
  selectedAssessedAt,
  onSelect,
}: AssessmentRunBarProps): JSX.Element => {
  const [open, setOpen] = useState(false);

  const selectedRun = runs.find((r) => r.assessed_at === selectedAssessedAt);
  const flagged = (view?.sinsFlagged ?? 0) > 0;

  const list = (
    <List>
      <ListHeader>
        {runs.length} {runs.length === 1 ? "run" : "runs"} · newest first
      </ListHeader>
      {runs.map((run) => {
        const vm = buildViewModel(run);
        const runFlagged = vm.sinsFlagged > 0;
        return (
          <RunRow
            key={run.assessed_at}
            $active={run.assessed_at === selectedAssessedAt}
            onClick={(): void => {
              onSelect(run.assessed_at);
              setOpen(false);
            }}
          >
            <Lines>
              <Primary>{dayjs(run.assessed_at).format(DATE_TIME_FORMAT)}</Primary>
              <Secondary>
                {run.model_version} · v{run.prompt_version}
              </Secondary>
            </Lines>
            <Spacer />
            <StateSegments states={vm.pillarRows.map((p) => p.state)} />
            <Secondary>
              <FlagMark $flagged={runFlagged}>
                {runFlagged ? <FlagGlyph size={10} /> : <CheckOutlined />}
              </FlagMark>
              {runFlagged ? vm.sinsFlagged : "Clean"}
            </Secondary>
          </RunRow>
        );
      })}
    </List>
  );

  return (
    <Popover
      content={list}
      trigger="click"
      placement="bottomLeft"
      open={open}
      onOpenChange={setOpen}
      arrow={false}
    >
      <Bar role="button" tabIndex={0} aria-expanded={open}>
        <Item>
          {view ? (
            <FlagStack flags={view.sinRows.map((s) => s.isFlagged)} />
          ) : (
            <FlagMark $flagged={false}>
              <CheckOutlined />
            </FlagMark>
          )}
          {flagged ? `${view?.sinsFlagged ?? 0} flagged` : "Clean"}
        </Item>

        <Divider />

        <Item>
          <Icon>
            <HistoryOutlined />
          </Icon>
          {runs.length} {runs.length === 1 ? "run" : "runs"}
          <Chevron $open={open}>
            <DownOutlined />
          </Chevron>
        </Item>

        <Divider />

        <Item
          title={
            selectedRun
              ? dayjs(selectedRun.assessed_at).format(DATE_TIME_FORMAT)
              : undefined
          }
        >
          {selectedRun
            ? dayjs(selectedRun.assessed_at).format(DATE_FORMAT)
            : "—"}
          {selectedRun && (
            <Muted>{dayjs(selectedRun.assessed_at).fromNow()}</Muted>
          )}
        </Item>

        <Divider />

        <Item>
          <Mono>{selectedRun?.model_version ?? "—"}</Mono>
          {selectedRun && <Muted>v{selectedRun.prompt_version}</Muted>}
        </Item>

        <Divider />

        <Tooltip
          title={
            view
              ? `${view.pillarsMet} of ${view.pillarsTotal} pillars met`
              : undefined
          }
        >
          <Item>
            <StateSegments
              states={view?.pillarRows.map((p) => p.state) ?? []}
            />
            {view && (
              <Muted>
                {view.pillarsMet}/{view.pillarsTotal}
              </Muted>
            )}
          </Item>
        </Tooltip>
      </Bar>
    </Popover>
  );
};

type AssessmentRunBarProps = {
  runs: EngagementAssessmentV2Run[];
  view: AssessmentViewModel | null;
  selectedAssessedAt: string | null;
  onSelect: (assessedAt: string) => void;
};
